import { sanitizeHtml } from "@/lib/sanitize";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listVendors } from "@/lib/catalog.functions";

export const Route = createFileRoute("/_authenticated/ustvarjalci")({
  head: () => ({
    meta: [
      { title: "Prodajalci — Admin" },
      { name: "description", content: "Seznam prodajalcev (admin)." },
    ],
  }),
  component: Page,
});

function Page() {
  const fetchVendors = useServerFn(listVendors);
  const { data, isLoading } = useQuery({ queryKey: ["vendors"], queryFn: () => fetchVendors() });

  const vendors = data?.vendors ?? [];
  const active = vendors.filter((v) => v.status === "active");
  const others = vendors.filter((v) => v.status !== "active");

  return (
    <section className="mx-auto max-w-7xl px-6 py-16">
      <div className="eyebrow">Skrbniška plošča</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-3">Naši prodajalci</h1>
      <p className="mt-4 text-muted-foreground max-w-xl">Majhne delavnice in samostojni obrtniki iz vse Slovenije.</p>

      {isLoading ? (
        <div className="mt-16 text-center text-muted-foreground">Nalagam…</div>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <h2 className="font-serif text-2xl mt-12 mb-4">Aktivni prodajalci</h2>
              <VendorGrid vendors={active} />
            </>
          )}
          {others.length > 0 && (
            <>
              <h2 className="font-serif text-2xl mt-16 mb-4 text-muted-foreground">Arhiv</h2>
              <VendorGrid vendors={others} />
            </>
          )}
        </>
      )}
    </section>
  );
}

function VendorGrid({ vendors }: { vendors: Array<{ id: string; slug: string | null; store_name: string; city: string | null; description: string | null; product_count: number; status: string }> }) {
  return (
    <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
      {vendors.map((s) => (
        <Link
          key={s.id}
          to="/prodajalci/$slug"
          params={{ slug: s.slug ?? "" }}
          className="bg-background p-8 hover:bg-muted/40 transition-colors group"
        >
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-serif text-2xl group-hover:text-accent transition-colors">{s.store_name}</h2>
              {s.city && <div className="text-xs text-muted-foreground mt-1">{s.city}</div>}
            </div>
            {s.status === "active" && <span className="text-xs text-accent">● aktiven</span>}
          </div>
          {s.description && (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.description) }} />
          )}
          <div className="mt-6 eyebrow">{s.product_count} izdelkov →</div>
        </Link>
      ))}
    </div>
  );
}
